import { Box, Flex, Text, VStack } from 'tcm-chakra-ui'
import { AthenaLinkButton } from '../shared/AthenaLinkButton'

export type WizardStep = {
  label: string
  description: string
}

type FollowUpStepperProps = {
  currentStep: number
  steps: WizardStep[]
  sessionId: string
  patientName?: string | null
  athenaPatientId?: string | null
  athenaTableSpace?: string | null
  emrLink?: string | null
}

export function FollowUpStepper({
  currentStep,
  steps,
  sessionId,
  patientName,
  athenaPatientId,
  athenaTableSpace,
  emrLink,
}: FollowUpStepperProps) {
  const showPatientBar = currentStep > 2 && Boolean(patientName)
  return (
    <Box>
      <Box position="relative">
        <Box
          display={{ base: 'none', md: 'block' }}
          position="absolute"
          top="1.25rem"
          left="10%"
          right="10%"
          borderTop="1px solid"
          borderColor="border.gray"
          zIndex={0}
        />
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          gap={{ base: 4, md: 3 }}
          position="relative"
          zIndex={1}
        >
          {steps.map((step, index) => {
            const stepNumber = index + 1
            const isActive = stepNumber === currentStep
            const isComplete = stepNumber < currentStep

            return (
              <VStack
                key={step.label}
                align="center"
                gap={2}
                flex="1"
                textAlign="center"
              >
                <Box
                  minW="2.5rem"
                  h="2.5rem"
                  display="grid"
                  placeItems="center"
                  borderRadius="full"
                  border="1px solid"
                  borderColor={
                    isActive || isComplete ? 'brand.500' : 'border.subtle'
                  }
                  bg={
                    isActive ? 'brand.500' : isComplete ? 'brand.50' : 'gray.50'
                  }
                  color={
                    isActive
                      ? 'white'
                      : isComplete
                        ? 'text.brandLight'
                        : 'text.secondary'
                  }
                  fontWeight="700"
                  boxShadow={isActive ? 'sm' : undefined}
                >
                  {stepNumber}
                </Box>
                <Text
                  fontSize="md"
                  fontWeight={isActive ? '700' : '600'}
                  color="text.brandDark"
                >
                  {step.label}
                </Text>
                <Text
                  fontSize="sm"
                  color="text.secondary"
                  maxW={{ base: '100%', md: '11rem' }}
                >
                  {step.description}
                </Text>
              </VStack>
            )
          })}
        </Flex>
      </Box>

      <Flex justify="space-between" align="center" mt={3}>
        {showPatientBar ? (
          <Flex align="center" gap={2}>
            <Text fontSize="xs" fontWeight="600" color="text.brandDark">
              {patientName}
            </Text>
            <AthenaLinkButton
              patientId={athenaPatientId}
              tableSpace={athenaTableSpace}
              emrLink={emrLink}
            />
          </Flex>
        ) : (
          <Box />
        )}
        <Text fontSize="xs" fontWeight="600" color="text.brandDark">
          Encounter ID:{' '}
          <Text as="span" fontFamily="mono" userSelect="all">
            {sessionId}
          </Text>
        </Text>
      </Flex>
    </Box>
  )
}
